import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Reservation, Person } from '@turizi/shared';
import PaxGroupList from '@/components/pax-groups/pax-group-list';
import { IconEyeEdit, IconAlertTriangle, IconMail, IconHandClick } from '@tabler/icons-react';
import { Button, Loader, Tooltip } from '@mantine/core';
import {
    useReservationEmailThreads
} from '@/hooks/api-hooks';
import ReservationAfipVouchers from '@/components/reservation/reservation-afip-vouchers';
import { formatDayLong, formatDayShort, formatPrice, setWebTitle } from '@/lib/format-utils';
import ReservationDatePicker from '@/components/reservation-date-picker';
import SelectServiceCreateBooking from '@/components/service-booking/select-service-available-create-booking';
import BookingSteps from '@/components/service-booking/booking-steps';

import ReservationPriceTotals from '@/components/reservation-price-totals';
import ReservationOriginalPriceTotals from '@/components/reservation-original-price-totals';
import EditReservationBookings from '@/components/edit-reservation-bookings';
import ForceReservationData from '@/components/force-reservation-data';
import { ReservationPaymentStatus, ReservationStatusBadge } from '@/components/reservation-ui';
import ReservationCoordinatorSelector from '@/components/service-booking/reservation-coordinator-selector';
import ParentReservationMinimap from '@/components/parent-reservation-minimap';
import { TagIcon } from '@/lib/tag-utils';
import { BUSINESSES_PATH } from '@/lib/navigation';
import ModalReservationEmails from '@/components/reservation/modal-reservation-emails';


interface Props {
    reservation: Reservation;
    disabled?: boolean;
}


export default function ReservationEditPage({ reservation, disabled = false }: Props) {
    const { t } = useTranslation();
    const id = reservation?.id;

    const [searchParams, setSearchParams] = useSearchParams();
    const [showSelectService, setShowSelectService] = useState(false);
    const [bookingDetails, setBookingDetails] = useState<number | undefined>();
    const [openEmailsModal, setOpenEmailsModal] = useState(false);
    const [selectedEmailThreadId, setSelectedEmailThreadId] = useState<string | null>(null);


    const { data: emailThreadsData } = useReservationEmailThreads(id);
    const rawEmailThreads = (emailThreadsData as any)?.data || [];
    const linkedEmailThreads = useMemo(() => {
        if (!Array.isArray(rawEmailThreads)) return [];
        const seenIds = new Set<string>();
        const seenSubjects = new Set<string>();
        const unique: any[] = [];

        for (const th of rawEmailThreads) {
            const idKey = (th.gmail_thread_id || '').toLowerCase();
            const subjKey = (th.subject || '').trim().toLowerCase();

            const isDuplicateId = Boolean(idKey && seenIds.has(idKey));
            const isDuplicateSubj = Boolean(subjKey && seenSubjects.has(subjKey));

            if (isDuplicateId || isDuplicateSubj) {
                continue;
            }

            if (idKey) seenIds.add(idKey);
            if (subjKey) seenSubjects.add(subjKey);
            unique.push(th);
        }
        return unique;
    }, [rawEmailThreads]);

    const paxId = searchParams.get('paxId');
    const bookings = reservation?.service_bookings || [];
    const filteredBookings = paxId
        ? (paxId.startsWith('group-')
            ? bookings.filter((b) => b.pax_groups?.some((bg) => bg.pax_group_id === Number(paxId.replace('group-', ''))))
            : bookings.filter((b) =>
                b.pax_groups?.some((bg) => {
                    if (bg.pax_group_id === Number(paxId)) return true;
                    if (Array.isArray(bg.booking_persons) && (bg.booking_persons.length > 0 || bg.is_custom_config)) {
                        return bg.booking_persons.some((bp) => bp.person_id === Number(paxId));
                    }
                    return bg.pax_group?.persons?.some((p) => p.id === Number(paxId));
                })
            ))
        : bookings;

    const validationErrors = useMemo(() => {
        if (!reservation?.service_bookings) return [];
        const errors: string[] = [];

        reservation?.service_bookings.forEach((booking) => {
            const costAmount = Number(booking.money_out_amount || 0);
            const sellAmount = Number(booking.money_in_amount || 0);
            const costCurrency = booking.money_out_currency || '$';
            const sellCurrency = booking.money_in_currency || '$';
            const hasProvider = Boolean(booking.selected_provider_id || booking.selected_provider);

            // 1. Booking with cost > 0 but no provider
            if (costAmount > 0 && !hasProvider) {
                errors.push(
                    t('validation_booking_no_provider_with_cost', {
                        bookingId: booking.id,
                        amount: formatPrice(costAmount),
                        currency: costCurrency,
                    })
                );
            }

            // 2. Booking cost higher than sell price
            if (costAmount > 0 && sellAmount > 0 && costAmount > sellAmount) {
                errors.push(
                    t('validation_booking_cost_higher_than_sell', {
                        bookingId: booking.id,
                        cost: formatPrice(costAmount, { currency: costCurrency }),
                        sellPrice: formatPrice(sellAmount, { currency: sellCurrency }),
                    })
                );
            }
        });

        return errors;
    }, [reservation?.service_bookings, t]);



    const clientName = reservation?.client_business?.name;

    setWebTitle(`${t('reservation')} #${id} - ${clientName || '...'} - ${formatDayShort(reservation?.start_day)} - ${formatDayShort(reservation?.end_day)}`);

    if (!reservation) return null;

    return (
        <div>

            <ForceReservationData reservation={reservation} />

            {/* allow multiple services selction here  */}
            {!disabled &&
                <>
                    <SelectServiceCreateBooking
                        reservation={reservation}
                        opened={showSelectService}
                        onClose={() => setShowSelectService(false)}
                        onCreate={(booking) => {
                            // do nothing for now
                        }}
                    />
                    <BookingSteps
                        serviceBookingId={bookingDetails}
                        opened={!!bookingDetails}
                        onClose={() => setBookingDetails(undefined)}
                    />
                </>
            }

            <div className="panel-content">
                {validationErrors.length > 0 && (
                    <div className="mb-6 space-y-2">
                        {validationErrors.map((errMsg, idx) => (
                            <div
                                key={idx}
                                className="flex items-start gap-2.5 bg-red-50/90 border border-red-200/90 text-red-950 rounded-xl p-3 text-xs font-medium shadow-2xs"
                            >
                                <IconAlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                                <div className="flex-1 leading-relaxed">{errMsg}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Enterprise Header Bar: Compact, High-Density Command Bar */}
                <div className="mb-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                        {/* Left / Top: Client Info & Booking Dates */}
                        <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-3 min-w-0">
                            {/* Section 1: Client Info & Type */}
                            <div className='data-card w-full md:w-90'>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t('client')}</span>
                                <Link
                                    to={`${BUSINESSES_PATH}/${reservation?.client_business?.id}`}
                                    className="flex items-center gap-2.5 group min-w-0 md:w-90 "
                                >
                                    <TagIcon
                                        name={reservation?.client_business?.category}
                                        hideText
                                        noBackground
                                        size="sm"
                                        className="shrink-0"
                                    />
                                    <div className="min-w-0">
                                        <div className="text-md font-extrabold text-blue-600 group-hover:underline max-w-[180px] sm:max-w-[240px]">
                                            {clientName || '...'}
                                        </div>
                                        <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider leading-none mt-0.5">
                                            {t(reservation?.client_business?.category)}
                                        </div>
                                    </div>
                                </Link>
                            </div>

                            {/* Section 2: Status, Date Range & Parent Link */}
                            <div className="flex flex-wrap items-center gap-2 sm:gap-2.5 min-w-0">
                                <ReservationStatusBadge showText={false} reservation={reservation} className="text-xxs font-bold shrink-0" />

                                <div className="shrink-0 max-w-full">
                                    <ReservationDatePicker
                                        className="bg-slate-50 hover:bg-slate-100/90 border border-slate-200/80 rounded-lg px-2 sm:px-2.5 py-1 text-md font-semibold text-slate-800 transition-all cursor-pointer shadow-2xs"
                                        reservation={reservation}
                                        isLoading={false}
                                        showLabels={false}
                                        disabled={disabled}
                                    />
                                </div>

                                {reservation?.parent_id && (
                                    <div className="flex items-center gap-1.5 text-xs shrink-0">
                                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{t('derived_from_reservation')}:</span>
                                        <Link
                                            to={`/reservations/${reservation?.parent_id}`}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 hover:bg-blue-100 text-blue-600 font-bold text-xs rounded border border-blue-100 transition-colors"
                                        >
                                            #{reservation?.parent_id}
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* main section */}
                <div className="space-y-4">
                    <div className="flex flex-col md:flex-row gap-6">

                        {/* Sidebar */}
                        <div className="w-full md:w-90 space-y-4 shrink-0">

                            <div className="data-card space-y-3">
                                {/* Notes Section */}
                                {reservation?.observations && (
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t('observations')}</span>
                                        <div className='text-xs px-1 py-0.5 bg-red-50 text-gray-800 rounded text-wrap border border-red-100'>
                                            {reservation?.observations}
                                        </div>
                                    </div>
                                )}
                                {/* External Code Section */}
                                {reservation?.external_code && (
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t('external_code')}</span>
                                        <div className='text-xs px-1 py-0.5 bg-blue-50 text-gray-800 rounded text-wrap border border-blue-100'>
                                            {reservation?.external_code}
                                        </div>
                                    </div>
                                )}
                                {/* Coordination */}
                                <div>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t('coordinator')}</span>
                                    <ReservationCoordinatorSelector
                                        reservation={reservation}
                                    />
                                </div>
                            </div>

                            {/* Paxs */}
                            <div className="data-card">
                                <PaxGroupList
                                    reservationId={Number(reservation?.id)}
                                    bookings={reservation?.service_bookings || []}
                                    activeFilterId={paxId}
                                    onFilterClick={(id) => {
                                        const params = new URLSearchParams(searchParams);
                                        if (id) {
                                            params.set('paxId', id);
                                        } else {
                                            params.delete('paxId');
                                        }
                                        setSearchParams(params, { replace: true });
                                    }}
                                    editMode={!disabled}
                                />
                            </div>

                            {/* Payment Status & Restaurant Ticket Price Summary */}
                            <div className="data-card space-y-3">
                                <ReservationPaymentStatus reservation={reservation} showPayments={true} />
                                <div className="pt-2 border-t border-gray-100">
                                    <ReservationPriceTotals reservation={reservation} size="md" />
                                </div>
                                <ReservationOriginalPriceTotals reservation={reservation} />
                            </div>

                            <div className="data-card flex gap-4 justify-around">
                                <div>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                        {t('payment_due_date')}
                                    </span>
                                    <span className="font-semibold text-slate-700 text-xs block">
                                        {formatDayLong(reservation?.payment_due_day, '-- / --- / ----')}
                                    </span>
                                </div>
                                <div className="min-w-0">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                        {t('receipt_number')}
                                    </span>
                                    {reservation?.tax_id ? (
                                        <Tooltip label={t('manual_entry')} withArrow>
                                            <span className="inline-flex items-center gap-1 font-semibold text-slate-700 text-xs bg-slate-100/80 border border-slate-200 px-1.5 py-0.5 rounded">
                                                <IconHandClick size={12} className="text-slate-400 shrink-0" />
                                                <span>{reservation.tax_id}</span>
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <span className="font-semibold text-slate-700 text-xs block">
                                            -
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Card: Linked AFIP Vouchers */}
                            <ReservationAfipVouchers reservation={reservation} />

                            {/* Card: Parent Reservation Bookings Minimap */}
                            {reservation?.parent_id && (
                                <ParentReservationMinimap parentId={reservation?.parent_id} />
                            )}

                            {/* Card: Linked Emails at bottom of column */}
                            {linkedEmailThreads.length > 0 && (
                                <div className="data-card">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">
                                        {t('linked_emails')}
                                    </span>
                                    <div className="space-y-1">
                                        {linkedEmailThreads.map((thread: any) => (
                                            <button
                                                key={thread.id || thread.gmail_thread_id}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedEmailThreadId(thread.gmail_thread_id || null);
                                                    setOpenEmailsModal(true);
                                                }}
                                                className="w-full flex items-center gap-1.5 px-2 py-1 bg-white hover:bg-gray-50 text-gray-800 rounded border border-gray-200 hover:border-gray-300 transition-colors text-left cursor-pointer group"
                                                title={thread.subject || t('no_subject')}
                                            >
                                                <IconMail size={13} className="text-[#EA4335] shrink-0" />
                                                <span className="text-[11px] font-medium text-gray-800 group-hover:text-black truncate flex-1">
                                                    {thread.subject || t('no_subject')}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="w-full md:flex-1 min-w-0 pb-[50%]">
                            <EditReservationBookings
                                showCategory={true}
                                bookings={filteredBookings}
                                reservation={reservation}
                                showReservation={false}
                                showPrice={true}
                                showCost={true}
                                showPaymentStatus={false}
                                showServiceOrder={true}
                                leftAction={(booking) => setBookingDetails(booking.id)}
                                leftSection={(
                                    <Button
                                        variant='subtle'
                                        size='xs'
                                    >
                                        <IconEyeEdit size={18} stroke={2} className='text-green-600' />
                                    </Button>
                                )}
                                editable={true}
                                hideServicePackage={false}
                                onAddServicesClick={() => setShowSelectService(true)}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {id && (
                <ModalReservationEmails
                    reservationId={Number(id)}
                    opened={openEmailsModal}
                    onClose={() => {
                        setOpenEmailsModal(false);
                        setSelectedEmailThreadId(null);
                    }}
                    initialThreadId={selectedEmailThreadId}
                />
            )}


        </div>
    );
}
